import { useEffect, useState, useCallback, type ChangeEvent } from 'react';
import { api, type Me, type KbArticle, type KbAnalytics } from '../lib/api';

const PRODUCTS = ['', 'WxCC', 'WxC', 'CUCM', 'UCCX', 'LGW', 'Analyzer', 'Platform'];

function scopePill(a: KbArticle) {
  if (a.status === 'DRAFT') return <span className="pill bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300">DRAFT</span>;
  if (a.scope === 'TENANT') return <span className="pill bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300">TENANT · {a.tenant_id}</span>;
  return <span className="pill bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300">GLOBAL</span>;
}

function Refs({ refs }: { refs?: string | null }) {
  let arr: Array<{ title?: string; url: string }> = [];
  try { arr = JSON.parse(refs || '[]'); } catch { /* ignore */ }
  if (!arr.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
      {arr.map((r, i) => (
        <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
           className="text-sm text-blue-600 hover:underline dark:text-blue-400">{r.title || r.url} ↗</a>
      ))}
    </div>
  );
}

const fieldCls = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

function Card({ a, isAdmin, onChanged }: { a: KbArticle; isAdmin?: boolean; onChanged?: () => void }) {
  const [vote, setVote] = useState<null | boolean>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [f, setF] = useState({
    title: a.title, product: a.product || '', category: a.category || '',
    symptom: a.symptom || '', root_cause: a.root_cause, fix: a.fix, keywords: a.keywords || '',
  });
  const set = (k: keyof typeof f) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setF((p) => ({ ...p, [k]: e.target.value }));
  async function send(helpful: boolean) {
    setVote(helpful);
    try { await api.kbFeedback(a.article_no, helpful); }
    catch { setVote(null); } // restore the 👍/👎 buttons so a failed vote is visibly not recorded
  }
  async function save() {
    if (!f.title.trim() || !f.root_cause.trim() || !f.fix.trim()) { setMsg('Title, Root cause and Fix are required.'); return; }
    // send only changed fields — kbUpdate is a partial in-place update (same article number)
    const fields: Partial<KbArticle> = {};
    (Object.keys(f) as Array<keyof typeof f>).forEach((k) => {
      const orig = String((a as unknown as Record<string, unknown>)[k] ?? '').trim();
      if (f[k].trim() !== orig) (fields as Record<string, string>)[k] = f[k].trim();
    });
    if (!Object.keys(fields).length) { setMsg('No changes to save.'); return; }
    setSaving(true); setMsg(null);
    try { await api.kbUpdate(a.article_no, fields); setEditing(false); onChanged?.(); }
    catch (e: any) { setMsg(`Update failed: ${e.message}`); }
    finally { setSaving(false); }
  }
  async function del() {
    if (!window.confirm(`Delete ${a.article_no}? It is archived (recoverable), not purged.`)) return;
    setSaving(true); setMsg(null);
    try { await api.kbDelete(a.article_no); onChanged?.(); }
    catch (e: any) { setMsg(`Delete failed: ${e.message}`); setSaving(false); }
  }
  if (editing) {
    return (
      <div className="rounded-xl border border-blue-200 bg-white p-4 shadow-sm dark:border-blue-800 dark:bg-gray-800">
        <div className="mb-2 flex items-center gap-2">
          <span className="rounded-md bg-blue-50 px-2 py-0.5 text-xs font-bold tracking-wide text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">{a.article_no}</span>
          <span className="text-xs text-gray-400">editing — same article number, in-place</span>
        </div>
        <div className="space-y-2">
          <input className={fieldCls} value={f.title} onChange={set('title')} placeholder="Title *" />
          <div className="flex flex-wrap gap-2">
            <select className={`${fieldCls} w-auto flex-1`} value={f.product} onChange={set('product')}>
              {PRODUCTS.map((p) => <option key={p} value={p}>{p || 'Product…'}</option>)}
            </select>
            <input className={`${fieldCls} w-auto flex-[2]`} value={f.category} onChange={set('category')} placeholder="Category" />
          </div>
          <textarea className={fieldCls} rows={2} value={f.symptom} onChange={set('symptom')} placeholder="Symptom" />
          <textarea className={fieldCls} rows={3} value={f.root_cause} onChange={set('root_cause')} placeholder="Root cause *" />
          <textarea className={fieldCls} rows={4} value={f.fix} onChange={set('fix')} placeholder="Fix *" />
          <input className={fieldCls} value={f.keywords} onChange={set('keywords')} placeholder="Keywords (comma separated)" />
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button onClick={() => { setEditing(false); setMsg(null); }} disabled={saving}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600">Cancel</button>
            {msg && <span className="text-xs text-red-600 dark:text-red-400">{msg}</span>}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <span className="rounded-md bg-blue-50 px-2 py-0.5 text-xs font-bold tracking-wide text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">{a.article_no}</span>
        {a.product && <span className="pill bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">{a.product}</span>}
        {a.category && <span className="pill bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">{a.category}</span>}
        {scopePill(a)}
        <span className="ml-auto text-xs text-gray-400">{a.created_at}</span>
      </div>
      <h3 className="mb-2 text-base font-semibold text-gray-900 dark:text-gray-100">{a.title}</h3>
      {a.symptom && (
        <div className="mb-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Symptom</div>
          <p className="text-sm text-gray-600 dark:text-gray-300">{a.symptom}</p>
        </div>
      )}
      <div className="mb-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Root cause</div>
        <p className="text-sm text-gray-700 dark:text-gray-200">{a.root_cause}</p>
      </div>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Fix</div>
        <p className="rounded-lg border border-emerald-100 bg-emerald-50 p-2 text-sm text-gray-800 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-gray-100">{a.fix}</p>
      </div>
      <Refs refs={a.refs} />
      <div className="mt-3 flex items-center gap-3 text-xs text-gray-400">
        {a.keywords && <span className="truncate">🔑 {a.keywords}</span>}
        {isAdmin && (
          <span className="flex items-center gap-1.5">
            <button onClick={() => setEditing(true)} disabled={saving}
              className="rounded border border-gray-300 px-2 py-0.5 text-blue-600 hover:bg-blue-50 dark:border-gray-600 dark:text-blue-400 dark:hover:bg-blue-900/30">✏️ Edit</button>
            <button onClick={del} disabled={saving}
              className="rounded border border-red-200 px-2 py-0.5 text-red-600 hover:bg-red-50 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-900/30">🗑 Delete</button>
            {msg && <span className="text-red-600 dark:text-red-400">{msg}</span>}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {vote === null ? (
            <>Helpful?
              <button onClick={() => send(true)} className="rounded px-2 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-700">👍</button>
              <button onClick={() => send(false)} className="rounded px-2 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-700">👎</button>
            </>
          ) : <span className="text-emerald-600 dark:text-emerald-400">Thanks for the feedback</span>}
        </span>
      </div>
    </div>
  );
}

function AnalyticsPanel({ data }: { data: KbAnalytics }) {
  const tot = data.searches?.total ?? 0; const zero = data.searches?.zero ?? 0;
  const tile = (label: string, val: string | number) => (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
      <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{val}</div>
      <div className="text-[11px] uppercase tracking-wider text-gray-400">{label}</div>
    </div>
  );
  return (
    <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
      <div className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-5">
        {tile(`Searches (${data.window_days}d)`, tot)}
        {tile('Zero-result', zero)}
        {tile('Resolved from KB', data.resolved_from_kb)}
        {tile('AI drafts', data.ai_drafts_created)}
        {tile('Pending drafts', data.pending_drafts)}
      </div>
      {data.zero_result_queries.length > 0 && (
        <div className="text-xs text-gray-500">
          <span className="font-semibold">Top no-result searches (documentation backlog):</span>{' '}
          {data.zero_result_queries.slice(0, 8).map((q) => `${q.query} (${q.hits})`).join(' · ')}
        </div>
      )}
    </div>
  );
}

function NewArticleForm({ onCreated }: { onCreated: () => void }) {
  const empty = { title: '', product: '', category: '', symptom: '', root_cause: '', fix: '', keywords: '' };
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setF((p) => ({ ...p, [k]: e.target.value }));
  async function save() {
    if (!f.title.trim() || !f.root_cause.trim() || !f.fix.trim()) { setMsg('Title, Root cause and Fix are required.'); return; }
    setSaving(true); setMsg(null);
    try {
      const body: Record<string, string> = {};
      (Object.keys(f) as Array<keyof typeof f>).forEach((k) => { if (f[k].trim()) body[k] = f[k].trim(); });
      const r = await api.kbCreate(body);
      setMsg(`Saved as ${r.article_no} ✔`); setF(empty); onCreated();
    } catch (e: any) { setMsg(`Save failed: ${e.message}`); }
    finally { setSaving(false); }
  }
  return (
    <details className="mb-4 rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
      <summary className="cursor-pointer text-sm font-semibold text-gray-700 dark:text-gray-200">➕ New KB article</summary>
      <div className="mt-3 space-y-2">
        <input className={fieldCls} value={f.title} onChange={set('title')} placeholder="Title *" />
        <div className="flex flex-wrap gap-2">
          <select className={`${fieldCls} w-auto flex-1`} value={f.product} onChange={set('product')}>
            {PRODUCTS.map((p) => <option key={p} value={p}>{p || 'Product…'}</option>)}
          </select>
          <input className={`${fieldCls} w-auto flex-[2]`} value={f.category} onChange={set('category')} placeholder="Category (e.g. Flow design)" />
        </div>
        <textarea className={fieldCls} rows={2} value={f.symptom} onChange={set('symptom')} placeholder="Symptom" />
        <textarea className={fieldCls} rows={2} value={f.root_cause} onChange={set('root_cause')} placeholder="Root cause *" />
        <textarea className={fieldCls} rows={3} value={f.fix} onChange={set('fix')} placeholder="Fix *" />
        <input className={fieldCls} value={f.keywords} onChange={set('keywords')} placeholder="Keywords (comma separated)" />
        <div className="flex items-center gap-2">
          <button onClick={save} disabled={saving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save article'}
          </button>
          {msg && <span className={`text-xs ${msg.includes('✔') ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{msg}</span>}
        </div>
      </div>
    </details>
  );
}

export default function KnowledgeBase({ me }: { me: Me }) {
  const isAdmin = me.role === 'tenant_admin' || me.role === 'super_admin';
  const [q, setQ] = useState('');
  const [product, setProduct] = useState('');
  const [showDrafts, setShowDrafts] = useState(false);
  const [rows, setRows] = useState<KbArticle[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Ask-the-KB
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<null | { answer: string; citations: Array<{ article_no: string; title: string }> }>(null);
  const [asking, setAsking] = useState(false);

  const [stats, setStats] = useState<KbAnalytics | null>(null);

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      if (showDrafts) { const r = await api.kbList(product || undefined, 'DRAFT'); setRows(r.results); }
      else if (q.trim()) { const r = await api.kbSearch(q.trim(), product || undefined); setRows(r.results); }
      else { const r = await api.kbList(product || undefined); setRows(r.results); }
    } catch (e: any) { setErr(e.message); setRows([]); }
    finally { setBusy(false); }
  }, [q, product, showDrafts]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { if (isAdmin) api.kbAnalytics(30).then(setStats).catch(() => {}); }, [isAdmin]);

  async function ask() {
    if (!question.trim()) return;
    setAsking(true); setAnswer(null);
    try { const r = await api.kbAsk(question.trim()); setAnswer({ answer: r.answer, citations: r.citations }); }
    catch (e: any) { setAnswer({ answer: `Could not reach the assistant: ${e.message}`, citations: [] }); }
    finally { setAsking(false); }
  }

  return (
    <div className="w-full px-4 py-6">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Knowledge Base</h1>
      <p className="mb-4 text-sm text-gray-500">
        Global, cross-tenant case library — keyword search, article #, date, root cause &amp; fix. GLOBAL articles are inherited by every tenant.
      </p>

      {/* Ask-the-KB */}
      <div className="mb-4 rounded-xl border border-blue-100 bg-blue-50/50 p-3 dark:border-blue-900/40 dark:bg-blue-900/10">
        <div className="flex flex-wrap gap-2">
          <input value={question} onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && ask()}
            placeholder="Ask a question — e.g. why does an agent get a popup before an outbound call?"
            className="min-w-[240px] flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800" />
          <button onClick={ask} disabled={asking}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {asking ? 'Asking…' : 'Ask the KB'}
          </button>
        </div>
        {answer && (
          <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3 text-sm dark:border-gray-700 dark:bg-gray-800">
            <p className="whitespace-pre-wrap text-gray-800 dark:text-gray-100">{answer.answer}</p>
            {answer.citations.length > 0 && (
              <div className="mt-2 text-xs text-gray-500">Sources: {answer.citations.map((c) => `${c.article_no}`).join(', ')}</div>
            )}
          </div>
        )}
      </div>

      {isAdmin && stats && <AnalyticsPanel data={stats} />}

      {isAdmin && <NewArticleForm onCreated={load} />}

      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 bg-gray-50 py-2 dark:bg-gray-900">
        <input value={q} onChange={(e) => setQ(e.target.value)} type="search" autoComplete="off"
          disabled={showDrafts}
          placeholder="Search symptoms, keywords, fixes…  (e.g. outdial answer popup)"
          className="min-w-[220px] flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800" />
        <select value={product} onChange={(e) => setProduct(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800">
          {PRODUCTS.map((p) => <option key={p} value={p}>{p || 'All products'}</option>)}
        </select>
        {isAdmin && (
          <label className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600">
            <input type="checkbox" checked={showDrafts} onChange={(e) => setShowDrafts(e.target.checked)} />
            Drafts to review{stats?.pending_drafts ? ` (${stats.pending_drafts})` : ''}
          </label>
        )}
      </div>

      <div className="mb-2 mt-1 text-xs text-gray-400">
        {busy ? 'Loading…' : rows ? `${rows.length} ${showDrafts ? 'draft' : 'article'}${rows.length === 1 ? '' : 's'}${q && !showDrafts ? ` matching “${q}”` : ''}` : ''}
      </div>

      {err && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">Could not reach the Knowledge Base: {err}</div>}

      <div className="space-y-3">
        {rows && rows.length === 0 && !busy && !err && <div className="py-10 text-center text-gray-400">{showDrafts ? 'No drafts awaiting review.' : 'No matching articles.'}</div>}
        {rows?.map((a) => <Card key={a.article_no} a={a} isAdmin={isAdmin} onChanged={load} />)}
      </div>
    </div>
  );
}
